/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { Report } from 'src/app/model';
import { StatStageType } from 'src/app/model/enums/stat.stage.type';
import { SystemType } from 'src/app/model/enums/system.type';
import { DelayType, ReportDelay } from 'src/app/model/report';
import { StatsReportData } from 'src/app/model/stats-report.data';
import { StatsReportDataDetails } from 'src/app/model/stats-report.data.details';
import { ReportProvider, StatisticsProvider } from 'src/app/providers';
import { LogService } from 'src/app/services';

@Component({
  selector: 'app-get-completed-report-modal',
  templateUrl: './completed-report.modal.html',
  styleUrls: ['./completed-report.modal.scss']
})

export class GetCompletedReportModal implements OnInit, OnDestroy {
  subject: Subject<boolean>;
  subscription: any;

  reports: any[] = [];
  offset: number = 0;
  currentMode: SystemType;
  stage: StatStageType;
  DelayType = DelayType;
  StatStageType = StatStageType;
  loading: boolean = true;
  fromDate:string="";
  toDate:string="";
  dueTodayReports:any[]=[];

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private statisticProvider: StatisticsProvider,
    private logService :LogService
  ) { }

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });
  }

  ngOnDestroy(): void {

  }

  onSubmit(): void {
    this.bsModalRef.hide();
    this.subject.next(true);
    this.subject.complete();
  }
  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next(false);
    this.subject.complete();
  }

  loadCompletedReports(): void {
    this.loading = true;
    if (this.stage == StatStageType.COMPLETED || this.stage == StatStageType.FIRST_DRAFT || this.stage == StatStageType.SUPPLEMENTAL_RECEIVED) {
      this.statisticProvider.getReportStats(this.currentMode, this.offset, this.stage)
        .subscribe(data => {
          if (data.success) {
            this.reports = StatsReportData.parseArray(data.result);
          }
          this.loading = false;
        }, err => {
          this.logService.error(err);
          this.loading = false;
        });
    }
    else if (this.stage == StatStageType.DUE_TODAY) {
      this.statisticProvider.getReportStatDueToday(this.currentMode)
        .subscribe(data => {
          if (data.success) {
            this.reports = StatsReportDataDetails.parseArray(data.result.DUE_TODAY);
            this.dueTodayReports = StatsReportDataDetails.parseArray(data.result.DELAYED_DUE_TODAY)
          }
          this.loading = false;
        }, err => {
          this.logService.error(err);
          this.loading = false;
        });
    }
    else if (this.stage == StatStageType.PAST_DUE) {
      this.statisticProvider.getReportStatPastDue(this.currentMode)
        .subscribe(data => {
          if (data.success) {
            this.reports = StatsReportDataDetails.parseArray(data.result);
          }
          this.loading = false;
        }, err => {
          this.logService.error(err);
          this.loading = false;
        });
    }
    else if (this.stage == StatStageType.ANALYST_OPEN_FILES) {
      this.statisticProvider.getReportStatAnalystOpenFile(this.currentMode)
        .subscribe(data => {
          if (data.success) {
            this.reports = StatsReportDataDetails.parseArray(data.result);
          }
          this.loading = false;
        }, err => {
          this.logService.error(err);
          this.loading = false;
        });
    }


    else if (this.stage == StatStageType.DUE_TOMORROW) {
      this.statisticProvider.getReportStatDueTomorrow(this.currentMode)
      .subscribe(data => {
        if(data.success){
          this.reports = StatsReportDataDetails.parseArray(data.result);
        }
        this.loading = false;
       }, err => {
        this.logService.error(err);
        this.loading = false;
      });
    }

    else if (this.stage == StatStageType.COMPLETED_REVENUE) {
      this.statisticProvider.getReportRevenueStats(this.currentMode, this.offset, this.stage)
        .subscribe(data => {
          if (data.success) {
            this.reports = StatsReportData.parseArray(data.result);
          }
          this.loading = false;
        }, err => {
          this.logService.error(err);
          this.loading = false;
        });
    }

   

  }
  formatDate(): void{
    if(this.fromDate!="" && this.toDate!=""){
      this.fromDate=moment(this.fromDate).format('MM/DD/YYYY');
      this.toDate=moment(this.toDate).format('MM/DD/YYYY');
      this.loadCompletedReports();
    }
  }

  getDelay(delay: ReportDelay[]): string {
    let str: string[] = [];

    if (delay.length == 0)
      return "N/A";

    for (let d of delay) {
      if (d.DELAY_TYPE == DelayType.CLIENT) {
        if (!str.find(s => s == "Client"))
          str.push("Client");
      }
      else if (d.DELAY_TYPE == DelayType.RELIANT) {
        if (!str.find(s => s == "Reliant"))
          str.push("Reliant");
      }
    }

    return str.join(", ").toString();
  }

  getTotalReport(): number {
    let total = 0;
    if (this.reports?.length == 0)
      return 0;

    for (let r of this.reports) {
      total += r.TOTAL_REPORT;
    }

    return total;
  }

  getTotalReliant(): number {
    let total = 0;
    if (this.reports?.length == 0)
      return 0;

    for (let r of this.reports) {
      total += r.TOTAL_RELIANT;
    }

    return total;
  }

  getTotalClient(): number {
    let total = 0;
    if (this.reports?.length == 0)
      return 0;

    for (let r of this.reports) {
      total += r.TOTAL_CLIENT;
    }

    return total;
  }

}
